import { useCallback } from 'react';

import { type PublicClip } from '@air/api';
import { type AssetCustomFieldValue, CustomFieldTypeName } from '@air/api/types';

import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { PublicAssetCustomFieldDateInput } from '~/components/RightPanel/CustomFields/PublicAssetCustomFieldDateInput';
import { PublicAssetCustomFieldPlainTextInputTableCell } from '~/components/RightPanel/CustomFields/PublicAssetCustomFieldPlainTextInputTableCell';
import { PublicAssetCustomFieldSelect } from '~/components/RightPanel/CustomFields/PublicAssetCustomFieldSelect';
import { BaseCustomFieldTableCell } from '~/components/TableView/TableRow/CustomFieldTableCell/BaseCustomFieldTableCell';
import { type AssetCustomFieldTableCellProps } from '~/components/TableView/TableRow/CustomFieldTableCell/types';
import { usePublicCustomFieldsContext } from '~/providers/PublicCustomFieldsProvider';
import { type AssignedCustomFieldValue } from '~/swr-hooks/customFields/types';
import { canChangeAssetCustomFields } from '~/utils/permissions/assetPermissions';

export const PublicAssetCustomFieldTableCell = ({
  clip,
  customFieldId,
  customFieldName,
}: AssetCustomFieldTableCellProps<PublicClip>) => {
  const actualCustomFieldValue = clip.customFields?.find((customField) => customField.id === customFieldId);
  // biome-ignore lint/plugin: the public clip custom field is narrowed to the assigned value shape used by the cell renderer
  const assignedCustomFieldValue = actualCustomFieldValue as AssignedCustomFieldValue | undefined;
  const { permissions } = usePublicPermissions();
  const canEditCustomFields = canChangeAssetCustomFields(permissions);
  const { customFields } = usePublicCustomFieldsContext();
  const customField = customFields?.find((field) => field.id === customFieldId);

  const renderEditableCustomField = useCallback(
    (closeEditor: () => void) => {
      if (!customField) {
        return null;
      }
      if (!canEditCustomFields) {
        return null;
      }
      const assetCustomFieldValue: AssetCustomFieldValue = actualCustomFieldValue || {
        ...customField,
        assetId: clip.assetId,
        value: null,
        values: null,
      };
      switch (customField.type.title) {
        case CustomFieldTypeName.singleSelect:
        case CustomFieldTypeName.multiSelect: {
          return (
            <PublicAssetCustomFieldSelect
              autoFocus
              assetId={clip.assetId}
              canEditCustomFields={canEditCustomFields}
              onOptionsClose={closeEditor}
              field={assetCustomFieldValue}
              trackingLocation="table-cell"
              className="ads:bg-base bg-gray-1"
            />
          );
        }
        case CustomFieldTypeName.plainText: {
          return (
            <PublicAssetCustomFieldPlainTextInputTableCell
              assetId={clip.assetId}
              field={assetCustomFieldValue}
              closeEditor={closeEditor}
              trackingLocation="table-cell"
            />
          );
        }
        case CustomFieldTypeName.date: {
          return (
            <PublicAssetCustomFieldDateInput
              isEditing
              assetId={clip.assetId}
              canEditCustomFields={canEditCustomFields}
              field={assetCustomFieldValue}
              onBlur={closeEditor}
              trackingLocation="table-cell"
            />
          );
        }
      }
    },
    [customField, canEditCustomFields, actualCustomFieldValue, clip.assetId],
  );

  return (
    <BaseCustomFieldTableCell
      customField={customField}
      customFieldId={customFieldId}
      customFieldName={customFieldName}
      assignedCustomFieldValue={assignedCustomFieldValue}
      renderEditableCustomField={canEditCustomFields ? renderEditableCustomField : undefined}
    />
  );
};
